import React, { useState } from 'react';

import { useParams } from 'src/navigation/hooks/useParams';

import {
    OnAddTooltipCordType,
    TooltipsType,
    TooltipType,
} from 'src/components/ReviewViewer/Tooltips/types';

import {
    decodeTooltipOptionsURL,
    decodeTooltipsURL,
    encodeTooltipsURL,
    getCoords,
} from './helpers';
import { TooltipOptions } from './types';
import { mockTooltips } from 'src/components/ReviewViewer/Tooltips/constants';

const useTooltips = (
    initialTooltips: TooltipsType,
    canvasRef: React.RefObject<HTMLCanvasElement>,
    onAddCord: OnAddTooltipCordType = () => {},
) => {
    const [params, setParams] = useParams();
    const tooltipsFromParams = params.tooltips;

    const [tooltips, setTooltips] = useState<TooltipsType>(
        decodeTooltipsURL(tooltipsFromParams) || [],
        // mockTooltips,
    );
    const [newTooltip, setNewTooltip] = useState<TooltipType | null>(null);
    const [options, setOptions] = useState<TooltipOptions>(
        decodeTooltipOptionsURL(tooltipsFromParams),
    );

    const createNewTooltip = (text: string) => {
        setNewTooltip({ text, coords: new Map() });
    };

    const addNewTooltipCoords = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        frameIndex: number,
    ): void => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        let moveHappened = false;
        const mouseMoveListener = () => {
            moveHappened = true;
            document.removeEventListener('mousemove', mouseMoveListener);
        };
        document.addEventListener('mousemove', mouseMoveListener);

        const mouseUpListener = () => {
            if (moveHappened) return;

            const newCoords = getCoords(event, canvas);

            setNewTooltip(prev => {
                if (!prev) return null;

                const coords = prev.coords.set(frameIndex, newCoords);
                onAddCord(coords);

                return {
                    ...prev,
                    coords,
                };
            });
            document.removeEventListener('mouseup', mouseUpListener);
        };
        document.addEventListener('mouseup', mouseUpListener);
    };

    const saveNewTooltip = () => {
        if (!newTooltip) return;

        const numberOfCoords = newTooltip.coords.size;
        if (numberOfCoords >= 2) {
            setTooltips(prev => {
                const tooltips = [...prev, newTooltip];

                setParams(
                    {
                        tooltips: encodeTooltipsURL(
                            tooltips,
                            options.hideWhileSpinning,
                            options.changeVisibilityNearExtremeFrames,
                        ),
                    },
                    { withReload: false },
                );

                return tooltips;
            });
        }

        setNewTooltip(null);
    };

    const removeNewTooltip = () => setNewTooltip(null);

    const deleteTooltip = (targetTooltipIndex: number) => {
        setTooltips(prev => {
            const tooltips = prev.filter((_, index) => index !== targetTooltipIndex);

            setParams(
                {
                    tooltips: tooltips.length
                        ? encodeTooltipsURL(
                              tooltips,
                              options.hideWhileSpinning,
                              options.changeVisibilityNearExtremeFrames,
                          )
                        : '',
                },
                { withReload: false },
            );

            return tooltips;
        });
    };

    const changeOptions = (newOptions: Partial<TooltipOptions>) => {
        setOptions(prev => {
            const options = { ...prev, ...newOptions };

            setParams(
                {
                    tooltips: tooltips.length
                        ? encodeTooltipsURL(
                              tooltips,
                              options.hideWhileSpinning,
                              options.changeVisibilityNearExtremeFrames,
                          )
                        : '',
                },
                { withReload: false },
            );

            return options;
        });
    };

    return {
        tooltips,
        newTooltip,
        setTooltips,
        createNewTooltip,
        addNewTooltipCoords,
        saveNewTooltip,
        removeNewTooltip,
        deleteTooltip,
        options,
        changeOptions,
    };
};
export { useTooltips };
